import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #isDraftCheckoutInFlight = false;
  #expressCheckoutSelector =
    "[data-draft-checkout-proxy], .additional-checkout-button, .shopify-payment-button button, .shopify-payment-button__button, shopify-accelerated-checkout-cart button, .amazon-payments-pay-button, .google-wallet-button-holder button";

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    document.addEventListener("submit", this.#handleCheckoutSubmit, true);
    document.addEventListener("click", this.#handleExpressCheckoutClick, true);
    document.addEventListener(
      "keydown",
      this.#handleExpressCheckoutKeydown,
      true,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    document.removeEventListener("submit", this.#handleCheckoutSubmit, true);
    document.removeEventListener("click", this.#handleExpressCheckoutClick, true);
    document.removeEventListener(
      "keydown",
      this.#handleExpressCheckoutKeydown,
      true,
    );
  }

  /**
   * Intercepts checkout submit and sends the user to draft order invoice URL.
   *
   * @param {SubmitEvent} event
   */
  #handleCheckoutSubmit = async (event) => {
    if (event.defaultPrevented) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== "cart-form") return;

    event.preventDefault();
    await this.#startDraftCheckout(event.submitter);
  };

  /**
   * Intercepts accelerated checkout button clicks and routes through draft-order pricing.
   *
   * @param {MouseEvent} event
   */
  #handleExpressCheckoutClick = async (event) => {
    if (event.defaultPrevented) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutButton = target.closest(this.#expressCheckoutSelector);
    if (!(checkoutButton instanceof HTMLElement)) return;
    if (!this.contains(checkoutButton)) return;

    const cartForm = document.getElementById("cart-form");
    if (!(cartForm instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();
    await this.#startDraftCheckout(checkoutButton);
  };

  /**
   * Allows keyboard activation for draft-checkout proxy containers.
   *
   * @param {KeyboardEvent} event
   */
  #handleExpressCheckoutKeydown = async (event) => {
    if (event.defaultPrevented) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutProxy = target.closest("[data-draft-checkout-proxy]");
    if (!(checkoutProxy instanceof HTMLElement)) return;
    if (!this.contains(checkoutProxy)) return;

    event.preventDefault();
    event.stopPropagation();
    await this.#startDraftCheckout(checkoutProxy);
  };

  /**
   * Runs the draft-order checkout flow.
   *
   * @param {HTMLElement | null | undefined} submitter
   */
  #startDraftCheckout = async (submitter) => {
    const submitterElement =
      submitter instanceof HTMLElement ? submitter : null;
    const loadingTarget =
      submitterElement?.closest("[data-draft-checkout-proxy='true']") ||
      submitterElement;

    if (this.#isDraftCheckoutInFlight) return;

    this.#isDraftCheckoutInFlight = true;
    this.#setDraftCheckoutLoadingState(loadingTarget, true);
    if (submitterElement && "disabled" in submitterElement) {
      /** @type {HTMLButtonElement | HTMLInputElement} */ (submitterElement)
        .disabled =
        true;
    }

    try {
      const cartResponse = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });

      if (!cartResponse.ok) {
        throw new Error("Unable to load cart data before draft checkout.");
      }

      const cartData = await cartResponse.json();
      /** @type {Array<{ variant_id: number; quantity: number; properties?: Record<string, unknown> }>} */
      const cartItems = Array.isArray(cartData && cartData.items)
        ? cartData.items
        : [];
      if (!cartItems.length) {
        throw new Error("Cart is empty.");
      }

      // Build items array — one entry per cart line
      let hasAppliedImageUploadFee = false;
      const items = cartItems.map((item) => {
        const quantity = Math.max(
          1,
          Number.parseInt(String(item.quantity || 1), 10) || 1,
        );
        let unitPriceCents = this.#getTierUnitPriceCents(item, quantity);

        if (!hasAppliedImageUploadFee && this.#hasImageUploadFee(item)) {
          unitPriceCents += 5000 / quantity;
          hasAppliedImageUploadFee = true;
        }

        return {
          variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity,
          unitPrice: unitPriceCents / 100,
          customAttributes: this.#buildCustomAttributes(item),
        };
      });

      const payload = { items };

      const res = await fetch("/apps/draft-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Draft checkout failed:", res.status, text);
        this.#notifyDraftCheckoutError();
        return;
      }

      const data = await res.json();
      if (data.invoiceUrl) {
        window.location.href = data.invoiceUrl;
      } else {
        console.error("Draft order failed", data);
        this.#notifyDraftCheckoutError();
      }
    } catch (error) {
      console.error("Draft checkout failed", error);
      this.#notifyDraftCheckoutError();
    } finally {
      if (submitterElement && "disabled" in submitterElement) {
        /** @type {HTMLButtonElement | HTMLInputElement} */ (
          submitterElement
        ).disabled = false;
      }
      this.#setDraftCheckoutLoadingState(loadingTarget, false);
      this.#isDraftCheckoutInFlight = false;
    }
  };

  /**
   * Toggles visual loading state while draft checkout is being created.
   *
   * @param {Element | null | undefined} target
   * @param {boolean} isLoading
   */
  #setDraftCheckoutLoadingState(target, isLoading) {
    const checkoutButton = document.getElementById("checkout");
    if (checkoutButton instanceof HTMLElement) {
      checkoutButton.classList.toggle("is-loading", isLoading);
      checkoutButton.setAttribute("aria-busy", isLoading ? "true" : "false");
    }

    const proxyContainers = document.querySelectorAll(
      "[data-draft-checkout-proxy='true']",
    );
    proxyContainers.forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      container.classList.toggle("is-loading", isLoading);
      container.setAttribute("aria-busy", isLoading ? "true" : "false");
    });

    if (target instanceof HTMLElement) {
      target.classList.toggle("is-loading", isLoading);
    }
  }

  #notifyDraftCheckoutError() {
    window.alert(
      "We could not start checkout right now. Please try again.",
    );
  }

  /**
   * Builds draft-order custom attributes from cart line-item properties.
   *
   * @param {{ properties?: Record<string, unknown> }} item
   * @returns {Array<{ key: string; value: string }>}
   */
  #buildCustomAttributes(item) {
    const properties = item && item.properties ? item.properties : {};
    const hiddenKeys = new Set([
      "Design storage",
      "Design ID",
      "Design file",
      "Design review link",
      "Applied sides",
    ]);

    return Object.entries(properties)
      .filter(([key, value]) => {
        const normalizedKey = String(key || "");
        if (!normalizedKey || normalizedKey.startsWith("_")) return false;
        if (hiddenKeys.has(normalizedKey)) return false;
        return value !== null && value !== undefined && String(value).trim() !== "";
      })
      .map(([key, value]) => ({
        key: String(key),
        value: String(value),
      }));
  }

  /**
   * @param {{ properties?: Record<string, unknown> }} item
   * @returns {boolean}
   */
  #isDesignItem(item) {
    const properties = item && item.properties ? item.properties : {};
    return (
      properties["Custom design"] === "Applied" ||
      !!properties._hat_ref ||
      !!properties._design_id
    );
  }

  /**
   * @param {{ properties?: Record<string, unknown> }} item
   * @returns {number}
   */
  #getUploadedDesignSideCount(item) {
    const properties = item && item.properties ? item.properties : {};
    const allowedViews = ["front", "back", "left", "right"];
    const uploadedViewsRaw = String(properties._hat_uploaded_views || "");

    if (uploadedViewsRaw) {
      const uploadedViews = uploadedViewsRaw
        .split(",")
        .map((view) =>
          String(view || "")
            .trim()
            .toLowerCase(),
        )
        .filter((view) => allowedViews.includes(view));

      if (uploadedViews.length) {
        return new Set(uploadedViews).size;
      }
    }

    return allowedViews.reduce((count, view) => {
      return count + (properties[`_hat_${view}_overlay`] ? 1 : 0);
    }, 0);
  }

  /**
   * @param {{ properties?: Record<string, unknown> }} item
   * @returns {boolean}
   */
  #hasImageUploadFee(item) {
    const properties = item && item.properties ? item.properties : {};
    const hasUploadFeeFlag = String(properties._hat_has_upload_fee || "")
      .trim()
      .toLowerCase();
    if (
      hasUploadFeeFlag === "1" ||
      hasUploadFeeFlag === "true" ||
      hasUploadFeeFlag === "yes"
    ) {
      return true;
    }

    const allowedViews = ["front", "back", "left", "right"];

    for (const view of allowedViews) {
      const overlay = properties[`_hat_${view}_overlay`];
      if (!overlay) continue;

      const sourceType = String(properties[`_hat_${view}_type`] || "")
        .trim()
        .toLowerCase();
      const imageSource = String(properties[`_hat_${view}_image_source`] || "")
        .trim()
        .toLowerCase();

      if ((!sourceType || sourceType === "image") && imageSource === "upload") {
        return true;
      }
    }

    const previewOverlay = properties._hat_preview_overlay;
    const previewType = String(properties._hat_preview_type || "")
      .trim()
      .toLowerCase();
    const previewImageSource = String(
      properties._hat_preview_image_source || "",
    )
      .trim()
      .toLowerCase();

    return (
      Boolean(previewOverlay) &&
      (!previewType || previewType === "image") &&
      previewImageSource === "upload"
    );
  }

  /**
   * @param {{ properties?: Record<string, unknown> }} item
   * @param {number} quantity
   * @returns {number}
   */
  #getTierUnitPriceCents(item, quantity) {
    const isDesignItem = this.#isDesignItem(item);

    if (isDesignItem) {
      let baseUnitPriceCents = 5000;
      if (quantity >= 144) baseUnitPriceCents = 2200;
      else if (quantity >= 73) baseUnitPriceCents = 2400;
      else if (quantity >= 49) baseUnitPriceCents = 2800;
      else if (quantity >= 24) baseUnitPriceCents = 3000;
      else if (quantity >= 12) baseUnitPriceCents = 3200;
      else if (quantity >= 6) baseUnitPriceCents = 3800;
      else if (quantity >= 2) baseUnitPriceCents = 4500;

      const uploadedSides = this.#getUploadedDesignSideCount(item);
      const additionalSides = Math.max(0, uploadedSides - 1);
      return baseUnitPriceCents + additionalSides * 1000;
    }

    if (quantity >= 144) return 1200;
    if (quantity >= 49) return 1300;
    if (quantity >= 24) return 1400;
    if (quantity >= 12) return 1500;
    if (quantity >= 6) return 1600;
    if (quantity >= 2) return 1800;
    return 2500;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key,
      ),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
        );

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", item.quantity.toString());

        // Update the quantity selector's internal state
        if (
          "updateCartQuantity" in selector &&
          typeof selector.updateCartQuantity === "function"
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      "cart-quantity-selector-component",
    )) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
